import React, { FC, useEffect, useState } from 'react';
import {
	alephToast,
	BackArrowIcon,
	BanIcon,
	Button,
	Dialog,
	Input,
	LoaderBars,
	NotificationToast,
	NotificationType,
	TrashCanIcon,
} from '@aleph/shared-components';
import { useMutation } from '@apollo/client';
import { DeleteUserDocument, RedditUsersListDocument, RedditUsersListQueryItems } from 'wb-reddit/graphql/generated';
import { HandleUpdateUserRow } from 'wb-reddit/app/routes/users/container';
import { Trans, useTranslation } from 'react-i18next';
import { AuthGraphUsersListQueryItems } from 'wb-spotify/graphql/authgraph/generated';

type CreateDialogProps = {
	isOpen: boolean;
	onClose: () => void;
	user: RedditUsersListQueryItems | AuthGraphUsersListQueryItems
	deleteRow: HandleUpdateUserRow
};

const DeleteDialog: FC<CreateDialogProps> = ({
	onClose, user, isOpen, deleteRow,
}) => {
	const { t } = useTranslation();

	const [userName, setUserName] = useState('');
	const [deleteUser, { loading: deleting, error, reset }] = useMutation(DeleteUserDocument, {
		errorPolicy:         'all',
		refetchQueries:      [RedditUsersListDocument],
		awaitRefetchQueries: true,
	});

	useEffect(() => {
		if (!isOpen) {
			setUserName('');
			reset();
		}
	}, [isOpen]);

	const onDelete = async () => {
		const userDeleted = await deleteUser({
			variables: {
				userId: user.userId,
			},
		});

		if (userDeleted?.data?.deleteUser) {
			deleteRow({
				// @ts-ignore: TODO provide type with mutations
				row:      user,
				isDelete: true,
			});
			onClose();
			alephToast(
				<NotificationToast
					type={NotificationType.Success}
					title={
						<div>
							<Trans
								i18nKey="sc: delete_dialog.success"
								defaults="<strong>{{name}} {{surname}}</strong> has been successfully deleted."
								values={{ name: user.userName, surname: user.userSurname }}
							>
								<strong>{user.userName} {user.userSurname}</strong> has been successfully deleted.
							</Trans>
						</div>
					}
				/>,
			);
		}
	};

	// @ts-ignore: not according to graphqlerror type
	const hasEntities = error?.graphQLErrors?.[0]?.form?.userId;
	// @ts-ignore: not according to graphqlerror type
	const userGroupNames: string[] = error?.graphQLErrors?.[1]?.userGroupNames ?? error?.graphQLErrors?.[2]?.userGroupNames ?? [];
	// @ts-ignore: not according to graphqlerror type
	const agencyName: string = error?.graphQLErrors?.[1]?.agencyAccountName ?? error?.graphQLErrors?.[2]?.agencyAccountName;

	const DeleteContent = (
		<div className="flex flex-col gap-[16px] pb-[16px]">
			<div className="text-gray-800 text-[16px]">
				<Trans
					i18nKey="sc: delete_dialog.title"
					defaults="Are you sure you want to delete <strong>{{name}} {{surname}}</strong>?"
					values={{ name: user.userName, surname: user.userSurname }}
				>
					Are you sure you want to delete <strong>{user.userName} {user.userSurname}</strong>?
				</Trans>
			</div>

			<div className="flex flex-col gap-[16px]">
				<div className="text-gray-800 font-bold text-[16px]">
					{t('sc: delete_dialog.unlock', 'Unlock confirmation')}
				</div>

				<Input
					value={userName}
					className="w-full"
					onChange={(e) => setUserName(e.target.value)}
					label={t('sc: username', 'User name')}
					instructionText={t('re: advertiser.ownership.transfer.instruction', 'Required to prevent accidental clicks')}
					disabled={deleting}
				/>
			</div>
		</div>
	);

	const ExistingContent = (
		<div className="flex flex-col gap-[16px] pb-[16px]">
			<div className="text-gray-800 text-[16px]">
				<strong>{user.userName} {user.userSurname}</strong> {t('sc: delete_dialog.john_doe', 'John Doe can not be deleted due to active ownership.')}
			</div>

			<div className="text-gray-800 text-[13px]">
				{t('sc: delete_dialog.transfer_entities', 'To delete the user, first transfer the following entities to other user(s).')}
			</div>

			<div>
				{userGroupNames?.map((groupName, idx) => (
					<div
						key={idx}
						className="h-[48px] border-b last-of-type:border-0 border-b-gray-300 flex items-center justify-between"
					>
						<div className="text-[16px] font-semibold">{groupName}</div>
						<div className="text-gray-600 text-[12px]">{t('sc: filters.entity.team', 'Team')}</div>
					</div>
				))}

				{
					agencyName
					&& <div className="h-[48px] border-b last-of-type:border-0 border-b-gray-300 flex items-center justify-between">
						<div className="text-[16px] font-semibold">{agencyName}</div>
						<div className="text-gray-600 text-[12px]">{t('sc: filters.entity.advertiser', 'Advertiser')}</div>
					</div>
				}
			</div>
		</div>
	);

	return (
		<Dialog
			isOpen={isOpen}
			onClose={onClose}
			className="!w-[548px]"
		>
			<Dialog.Header
				preset={
					{
						prefix: <TrashCanIcon />,
						title:  t('sc: delete_dialog.delete_user', 'Delete user'),
					}
				}
			/>

			<Dialog.Content>
				{hasEntities ? ExistingContent : DeleteContent}
			</Dialog.Content>

			<Dialog.Footer>
				<Button
					leftNode={<BanIcon />}
					onClick={onClose}
					disabled={deleting}
					appearance="outlined"
					color="gray"
					title={t('sc: cancel', 'Cancel')}
				/>

				{
					hasEntities ? <Button
						onClick={onClose}
						leftNode={<BackArrowIcon />}
						title={t('sc: ok', 'OK').toUpperCase()}
					/> : <Button
						loading={deleting}
						onClick={onDelete}
						leftNode={deleting ? <LoaderBars className="animate-spin" /> : <TrashCanIcon />}
						disabled={`${user.userName} ${user.userSurname}` !== userName || deleting}
						color="red"
						title={t('sc: delete', 'Delete')}
					/>
				}
			</Dialog.Footer>
		</Dialog>
	);
};

export default DeleteDialog;
